// EKH-VISTA-VIVA-01 · helpers PUROS de presentación del radar EKH.
// No interpretan clínicamente, no inventan unidades y no convierten una ausencia en un cero.

export const SIN_LECTURA = "sin_lectura";

export const CALIDAD_TEXTO = {
  candidata: "Lectura del fabricante",
  candidata_tiempo_no_acreditado: "Hora del equipo no acreditada",
  sin_lectura: "Sin lectura",
  invalida: "Valor no utilizable",
  no_verificable: "Dato no interpretable",
  conflicto_temporal: "Dos lecturas en el mismo instante",
  sin_atribucion_verificable: "Sin periodo verificable",
  desconocida: "Sin estado",
};

export function edadTexto(seg) {
  if (seg == null) return "—";
  if (seg < 60) return `hace ${Math.round(seg)} s`;
  if (seg < 3600) return `hace ${Math.round(seg / 60)} min`;
  if (seg < 86400) return `hace ${Math.round(seg / 3600)} h`;
  return `hace ${Math.round(seg / 86400)} d`;
}

export function duracionTexto(seg) {
  if (seg == null) return "—";
  const s = Math.max(0, Math.round(seg));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h} h ${m} min`;
  if (m) return `${m} min ${s % 60} s`;
  return `${s} s`;
}

// Texto de una métrica de frecuencia: el cero del fabricante NO se muestra como medición.
export function valorFrecuencia(m) {
  if (!m) return { texto: "—", leyenda: "Sin dato en esta lectura", dato: false };
  if (m.calidad === SIN_LECTURA) {
    return { texto: "Sin lectura", leyenda: CALIDAD_TEXTO[m.calidad], dato: false };
  }
  if (m.valor == null || typeof m.valor !== "number") {
    return { texto: "—", leyenda: CALIDAD_TEXTO[m.calidad] || "Sin estado", dato: false };
  }
  return { texto: `${m.valor}`, unidad: m.unidad || "", dato: true,
           leyenda: CALIDAD_TEXTO[m.calidad] || "" };
}

export function presenciaTexto(m) {
  if (!m || m.valor == null) {
    return { texto: "Sin dato", tono: "neutral", leyenda: CALIDAD_TEXTO[m?.calidad] || "" };
  }
  return m.valor
    ? { texto: "Persona detectada", tono: "live", leyenda: CALIDAD_TEXTO[m.calidad] || "" }
    : { texto: "Sin detección", tono: "neutral", leyenda: CALIDAD_TEXTO[m.calidad] || "" };
}

// Serie -> puntos para el gráfico, conservando los huecos como CORTES (nunca se interpolan).
export function segmentosSerie(puntos = [], huecoMs = 120000) {
  const segs = [];
  let actual = [];
  let prev = null;
  for (const p of puntos) {
    const t = Date.parse(p.t);
    if (!Number.isFinite(t)) continue;
    const usable = typeof p.valor === "number" && p.estado === "valida_candidata";
    if (prev != null && t - prev > huecoMs) {
      if (actual.length) segs.push(actual);
      actual = [];
    }
    prev = t;
    if (!usable) {
      if (actual.length) segs.push(actual);
      actual = [];
      continue;
    }
    actual.push({ t, v: p.valor });
  }
  if (actual.length) segs.push(actual);
  return segs;
}

// v2.20.1 · ¿esta cama está entregando señal AHORA? Una tarjeta sin señal no debe protagonizar el
// mosaico: se conserva visible, pero atenuada y al final.
export function conSenal(fila) {
  const i = fila?.instantaneas || {};
  const vivos = [i.heart_rate, i.respiratory_rate].some(
    (m) => typeof m?.valor === "number" && m.valor > 0 && m.calidad !== SIN_LECTURA);
  return vivos || i.presence?.valor === true;
}

// v2.20.2 · estado CLÍNICO de la cama en una sola línea para enfermería: en cama, moviéndose,
// fuera de cama o sin lectura. No se inventa: sin dato recibido se declara la ausencia.
export function estadoCama(fila) {
  const pres = fila?.instantaneas?.presence?.valor;
  const movSeg = fila?.ultimo_movimiento?.edad_seg;
  if (pres === true) {
    if (typeof movSeg === "number" && movSeg < 120) {
      return { clave: "moviendose", texto: "Moviéndose", tono: "gv2-chip-action" };
    }
    return { clave: "en_cama", texto: "En cama", tono: "gv2-chip-live" };
  }
  if (pres === false) {
    return { clave: "fuera_de_cama", texto: "Fuera de cama", tono: "gv2-chip-neutral" };
  }
  if (conSenal(fila)) return { clave: "con_lectura", texto: "Con lectura", tono: "gv2-chip-live" };
  return { clave: "sin_lectura", texto: "Sin lectura", tono: "gv2-chip-neutral" };
}

// v2.20.2 · ¿la lectura del equipo es RECIENTE? Un dato de hace horas no puede anunciarse como
// una señal en línea: la antigüedad manda sobre el valor.
export function lecturaReciente(fila, maxSeg = 120) {
  const i = fila?.instantaneas || {};
  const edades = [i.heart_rate, i.respiratory_rate, i.presence, fila?.ultimo_movimiento]
    .map((m) => m?.edad_seg)
    .filter((s) => typeof s === "number");
  return edades.length > 0 && Math.min(...edades) <= maxSeg;
}

export function rangoSimetrico(valores = []) {
  const max = valores.reduce((a, v) => Math.max(a, Math.abs(v)), 0);
  return max > 0 ? max : 1;
}
